import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, SelectField } from "@/components/common";
import { api } from "@/lib/api";
import { LINE_KINDS, LINE_BILLING, ORDER_STATUS, fmtPrice } from "@/lib/constants";

const num = (v) => (v === "" ? "" : Number(v));

export const LineDialog = ({ line, onClose, onSave }) => {
  const [f, setF] = useState({});
  useEffect(() => { setF(line || {}); }, [line]);
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v?.target ? v.target.value : v }));
  const ok = f.label && f.billing && f.quantity !== "" && f.unit_price !== "";
  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-cyan-400/20 bg-[#0F1F3D]" data-testid="billing-line-dialog">
        <DialogTitle>{f.id ? "Modifier la ligne" : "Ajouter une ligne"}</DialogTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type"><SelectField value={f.kind} onChange={set("kind")} options={LINE_KINDS} testid="line-kind-select" /></Field>
          <Field label="Facturation *"><SelectField value={f.billing} onChange={set("billing")} options={LINE_BILLING} placeholder="Choisir…" testid="line-billing-select" /></Field>
          <Field label="Désignation *" className="col-span-2"><Input value={f.label || ""} onChange={set("label")} data-testid="line-label-input" /></Field>
          <Field label="Description" className="col-span-2"><Input value={f.description || ""} onChange={set("description")} data-testid="line-description-input" /></Field>
          <Field label="Référence" className="col-span-2"><Input value={f.reference || ""} onChange={set("reference")} data-testid="line-reference-input" /></Field>
          <Field label="Quantité"><Input type="number" min={0} step="0.01" inputMode="decimal" value={f.quantity ?? ""} onChange={(e) => set("quantity")(num(e.target.value))} data-testid="line-quantity-input" /></Field>
          <Field label={f.kind === "remise" ? "Montant de la remise (€)" : "Prix unitaire (€)"}><Input type="number" min={0} step="0.01" inputMode="decimal" value={f.unit_price ?? ""} onChange={(e) => set("unit_price")(num(e.target.value))} data-testid="line-price-input" /></Field>
        </div>
        <p className="text-sm text-slate-300">Montant : {fmtPrice((Number(f.quantity) || 0) * (Number(f.unit_price) || 0))}</p>
        <Button disabled={!ok} onClick={() => onSave(f)} className="dt-glow h-11" data-testid="line-save-button">Enregistrer la ligne</Button>
      </DialogContent>
    </Dialog>
  );
};

const OrderRow = ({ o, pick, onChange }) => (
  <div className="rounded-lg border border-white/10 p-3" data-testid={`material-order-${o.id}`}>
    <label className="flex items-start gap-3">
      <Checkbox checked={!!pick} disabled={o.available <= 0 && !pick} onCheckedChange={(c) => onChange(c ? { quantity: Math.min(1, o.available) || 1, unit_price: o.sale_price ?? 0 } : null)} data-testid={`material-order-check-${o.id}`} className="mt-1 h-5 w-5" />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-white">{o.item}</p>
        <p className="text-xs text-slate-400">{o.reference || "Sans référence"} · {ORDER_STATUS[o.status]} · commandé {o.quantity} · disponible {o.available}{o.used_here ? ` · déjà sur ce bon ${o.used_here}` : ""}</p>
      </div>
    </label>
    {pick && (
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Field label="Quantité"><Input type="number" min={0} max={o.available} step="1" inputMode="decimal" value={pick.quantity} onChange={(e) => onChange({ ...pick, quantity: num(e.target.value) })} data-testid={`material-order-qty-${o.id}`} /></Field>
        <Field label="Prix de vente (€)"><Input type="number" min={0} step="0.01" inputMode="decimal" value={pick.unit_price} onChange={(e) => onChange({ ...pick, unit_price: num(e.target.value) })} data-testid={`material-order-price-${o.id}`} /></Field>
      </div>
    )}
  </div>
);

export const MaterialDialog = ({ interventionId, open, onClose, onAdd, onManual }) => {
  const [orders, setOrders] = useState([]);
  const [picks, setPicks] = useState({});
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    if (!open) return;
    setPicks({});
    setBilling(null);
    api.get(`/interventions/${interventionId}/billing/orders`).then((r) => setOrders(r.data));
  }, [open, interventionId]);
  const chosen = Object.entries(picks).filter(([, p]) => p && p.quantity > 0);
  const tooMuch = chosen.some(([id, p]) => p.quantity > (orders.find((o) => o.id === id)?.available ?? 0));
  const add = () => onAdd(chosen.map(([id, p]) => ({ order: orders.find((o) => o.id === id), ...p, billing })));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-cyan-400/20 bg-[#0F1F3D]" data-testid="material-dialog">
        <DialogTitle>Ajouter du matériel utilisé</DialogTitle>
        <p className="text-sm text-slate-400">Commandes de matériel de ce client. Seule la quantité choisie est reprise, la commande n'est jamais considérée comme entièrement utilisée.</p>
        <div className="space-y-2">
          {!orders.length && <p className="text-sm text-slate-400" data-testid="material-no-orders">Aucune commande de matériel pour ce client.</p>}
          {orders.map((o) => <OrderRow key={o.id} o={o} pick={picks[o.id]} onChange={(p) => setPicks((x) => ({ ...x, [o.id]: p }))} />)}
        </div>
        {chosen.length > 0 && (
          <Field label="Statut des produits sélectionnés *"><SelectField value={billing} onChange={setBilling} options={LINE_BILLING} placeholder="Choisir…" testid="material-billing-select" /></Field>
        )}
        {tooMuch && <p className="text-sm text-red-300">Quantité supérieure à la quantité disponible.</p>}
        <div className="grid gap-2 sm:grid-cols-2">
          <Button variant="outline" className="h-11" onClick={onManual} data-testid="material-manual-button">Ajouter sans commande</Button>
          <Button className="dt-glow h-11" disabled={!chosen.length || !billing || tooMuch} onClick={add} data-testid="material-add-button">Ajouter {chosen.length || ""} produit(s)</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
